import { Helmet } from "react-helmet-async";
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  type BaseSeoInput,
  type SupportedLocale,
} from "@/lib/seo";

type SeoHeadProps = BaseSeoInput & {
  type?: "website" | "article";
  /**
   * Optional JSON-LD blocks injected as <script type="application/ld+json">.
   * Build them via the helpers in src/lib/seo.ts so the serialisation
   * stays XSS-safe (Helmet writes via textContent, not innerHTML).
   */
  jsonLd?: Array<Record<string, unknown>>;
  /** Optional descriptive alt for og:image (defaults to the page description). */
  imageAlt?: string;
  /** Optional explicit OG image dimensions — recommended for social cards. */
  imageWidth?: number;
  imageHeight?: number;
  /**
   * When true, emit a machine-readable `<meta name="ai-generated">` marker.
   * EU AI Act Art. 50(2) — set on articles produced with AI assistance.
   */
  aiGenerated?: boolean;
};

const HTML_LANG_FOR: Record<SupportedLocale, string> = {
  fr: "fr-FR",
  en: "en-GB",
  es: "es-ES",
  ar: "ar",
};
const OG_LOCALE_FOR: Record<SupportedLocale, string> = {
  fr: "fr_FR",
  en: "en_GB",
  es: "es_ES",
  ar: "ar_AR",
};
const ALL_LOCALES: SupportedLocale[] = ["fr", "en", "es", "ar"];

// Default OG image is the static 1200×630 brand card. Override per-page via
// `image` + `imageWidth` + `imageHeight` when a richer share preview matters
// (case studies, articles).
const DEFAULT_OG_WIDTH = 1200;
const DEFAULT_OG_HEIGHT = 630;

export function SeoHead({
  title,
  description,
  path,
  locale = "fr",
  image,
  noindex = false,
  type = "website",
  jsonLd,
  imageAlt,
  imageWidth,
  imageHeight,
  aiGenerated = false,
}: SeoHeadProps) {
  const canonical = absoluteUrl(path);
  const ogImage = image ? absoluteUrl(image) : DEFAULT_OG_IMAGE;
  const fullTitle = title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;
  const htmlLang = HTML_LANG_FOR[locale];
  const altText = imageAlt ?? description;
  const ogWidth = imageWidth ?? (image ? undefined : DEFAULT_OG_WIDTH);
  const ogHeight = imageHeight ?? (image ? undefined : DEFAULT_OG_HEIGHT);

  // Same URL serves both locales — i18next picks language client-side from
  // localStorage / navigator. hreflang alternates therefore point at the
  // same path for fr + en + x-default; this is the documented pattern for
  // client-rendered language switching (see Google docs on hreflang).
  const hrefForLocale = `${SITE_URL}${path === "/" ? "" : path}`;

  return (
    <Helmet>
      <html lang={htmlLang} dir={locale === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"}
      />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      {aiGenerated && <meta name="ai-generated" content="true" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={altText} />
      {ogWidth !== undefined && <meta property="og:image:width" content={String(ogWidth)} />}
      {ogHeight !== undefined && <meta property="og:image:height" content={String(ogHeight)} />}
      <meta property="og:locale" content={OG_LOCALE_FOR[locale]} />
      {ALL_LOCALES.filter((l) => l !== locale).map((l) => (
        <meta key={l} property="og:locale:alternate" content={OG_LOCALE_FOR[l]} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={altText} />
      {ALL_LOCALES.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={hrefForLocale} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={hrefForLocale} />
      {jsonLd?.map((block, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
